import { getBackendUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import PageShell from "@/components/PageShell";
import { usePageContent } from "@/hooks/usePageContent";
import { useSEO } from "@/hooks/useSEO";
import RichHtml from "@/components/ui/RichHtml";
import axios from "axios";

const API = getBackendUrl();

export default function TermsPage() {
  const { get, loading: contentLoading } = usePageContent("terms");
  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    fetchSeoData();
  }, []);

  const fetchSeoData = async () => {
    try {
      const response = await axios.get(`${API}/api/seo/terms`);
      setSeoData(response.data);
    } catch (error) {
      console.error("Error fetching SEO data:", error);
    }
  };

  // Apply SEO
  useSEO(
    seoData ? {
      seo_title: seoData.seo_title || "Terms and Conditions | SellLocalHomes",
      seo_description: seoData.seo_description || "Read our terms and conditions.",
      seo_keywords: seoData.seo_keywords || "terms, conditions, legal",
      seo_robots: seoData.seo_robots || "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/terms"
    } : {
      seo_title: "Terms and Conditions | SellLocalHomes",
      seo_description: "Read our terms and conditions.",
      seo_keywords: "terms, conditions, legal",
      seo_robots: "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/terms"
    },
    { og_type: "website" }
  );

  // Default content if not loaded from CMS
  const defaultContent = `
    <h2>1. Introduction</h2>
    <p>Welcome to SellLocalHomes. These terms and conditions outline the rules and regulations for the use of our website.</p>
    
    <h2>2. Intellectual Property</h2>
    <p>Unless otherwise stated, SellLocalHomes owns the intellectual property rights for all material on this website.</p>
    
    <h2>3. Restrictions</h2>
    <p>You are specifically restricted from publishing any website material in any other media without permission.</p>
    
    <h2>4. Your Privacy</h2>
    <p>Please read our Privacy Policy for information on how we handle your personal data.</p>
    
    <h2>5. Disclaimer</h2>
    <p>Property information is provided by estate agents and may be subject to change. SellLocalHomes is not responsible for the accuracy of property listings.</p>
    
    <h2>6. Contact</h2>
    <p>If you have any questions about these Terms, please contact us.</p>
  `;

  const termsContent = get("page_content", defaultContent);

  return (
    <PageShell>
      <div className="min-h-screen" data-testid="terms-page">
      
      
      {/* Header Section */}
      <section className="pt-12 md:pt-16 pb-12 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-center">
            {get("page_title", "Terms and Conditions")}
          </h1>
          <p className="text-muted-foreground text-center mt-4">
            {get("last_updated", "Last updated: February 2026")}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <RichHtml
            className="prose prose-slate max-w-none
              prose-headings:font-serif prose-headings:text-foreground
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:border-b prose-h2:pb-2 prose-h2:border-border
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-muted-foreground prose-p:leading-relaxed
              prose-li:text-muted-foreground
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground
            "
            html={termsContent}
            data-testid="terms-content"
          />
        </div>
      </section>
      </div>
    </PageShell>
  );
}
